import { store } from 'store';

import { CASE_MANAGEMENT_DIVISOR, CASE_MANAGEMENT_PERCENTAGE } from '~constants/common';
import { SupportAtHomeFundingTypes } from '~constants/maps';

import dayjs, { dateInputFieldFormat, formatDateString } from '~libs/dayjs';

import { Row } from '~components/table-view-v3';

import { PricePlanModalRow } from '~support-at-home/models';

type Derive = (values: any) => any;
const currency = (value: number) => `$${Math.round(value).toLocaleString()}`;

export const derivePricePlan: Derive = ({ price_plan_id, price_plan_revision_id }) => ({
  price_plan_id,
  price_plan_revision_id,
});

export const deriveFormattedDateRange: Derive = ({ start_date, end_date }) => {
  if (!start_date || !end_date) return '';
  return `${formatDateString(start_date)} - ${formatDateString(end_date)}`;
};

export const deriveClaimStartDate: Derive = ({ end_date }) => {
  if (!end_date) return null;

  const date = dayjs(end_date);
  const SAH_QUARTER_START = '2025-10-01';
  const SAH_QUARTER_START_OVERRIDE = '2025-11-01';
  let start_date = date.startOf('quarter').format(dateInputFieldFormat);
  if (start_date === SAH_QUARTER_START) start_date = SAH_QUARTER_START_OVERRIDE;
  return { start_date, end_date };
};

export const deriveOriginLink: Derive = ({ origin_tag, origin_appointment_id, origin_supplier_invoice_item_id }) => {
  switch (origin_tag) {
    case 'invoice': {
      return `/support-at-home/suppliers?invoice-search={"items.id":"${origin_supplier_invoice_item_id}"}`;
    }
    case 'appointment': {
      return `/appointments/${origin_appointment_id}`;
    }
    default: {
      return null;
    }
  }
};

export const showCaseManagement: Derive = ({ package_fixed_id }) => {
  const packageSchedule = findPackageSchedule(package_fixed_id);
  return packageSchedule?.package?.funding_type?.sah_code === SupportAtHomeFundingTypes.SAH_QUARTERLY_CLIENT;
};

export const showFundingLevelAmounts: Derive = ({ client_id }) => {
  return hasSahBudgets(client_id) === false;
};

export const deriveFundingType: Derive = ({ package_fixed_id }) => {
  const packageSchedule = findPackageSchedule(package_fixed_id);
  return packageSchedule?.package?.funding_type?.name;
};

export const deriveClientWarning: Derive = ({ client_id }) => {
  if (!client_id) return [];
  const { clientServices, clientContributions } = store.getState().supportAtHome;

  const warnings: string[] = [];

  const hasServices = clientServices.rows.some(({ client_id: id }) => id === client_id);
  const hasContributions = clientContributions.rows.some(({ client_id: id }) => id === client_id);

  if (!hasSahBudgets(client_id)) warnings.push('No SaH Budgets found for this client');
  if (!hasServices) warnings.push('No SaH Approved Services found for this client');
  if (!hasContributions) warnings.push('No SaH Co-Contributions found for this client');

  return warnings;
};

export const deriveCaseManagementValues: Derive = ({ total_includes_case_management, total_amount }) => {
  const parsedAmount = parseFloat(total_amount);

  if (isNaN(parsedAmount) || parsedAmount <= 0) {
    return {
      baseAmount: 0,
      caseManagementAmount: 0,
      baseAvailableAmount: 0,
    };
  }

  // Calculate base amount and case management amount based on whether CM is included
  let baseAmount: number;
  let caseManagementAmount: number;

  if (total_includes_case_management == null) {
    // If undefined or null: return base amount only, no partition of funds
    baseAmount = parsedAmount;
    caseManagementAmount = 0;
  } else if (total_includes_case_management) {
    // If CM is included in total: 10% of total is CM, 90% is base
    caseManagementAmount = parsedAmount * CASE_MANAGEMENT_PERCENTAGE;
    baseAmount = parsedAmount - caseManagementAmount;
  } else {
    // If CM is not included: total is the base, CM is added such that it equals 10% of the final total
    baseAmount = parsedAmount;
    caseManagementAmount = parsedAmount / CASE_MANAGEMENT_DIVISOR;
  }

  // Calculate base available amount (base + case management)
  const baseAvailableAmount = baseAmount + caseManagementAmount;

  return {
    baseAmount,
    caseManagementAmount,
    baseAvailableAmount,
  };
};

export const deriveCaseManagementWarning: Derive = ({ total_includes_case_management, total_amount }) => {
  const parsedAmount = parseFloat(total_amount);

  if (isNaN(parsedAmount) || parsedAmount <= 0) {
    return 'Please enter a valid total amount greater than zero.';
  }

  const { baseAmount, caseManagementAmount, baseAvailableAmount } = deriveCaseManagementValues({
    total_includes_case_management,
    total_amount,
  });

  return `The base amount is ${currency(baseAmount)}. A 10% case management fee of ${currency(caseManagementAmount)} applies, making the total available amount ${currency(baseAvailableAmount)}.`;
};

export const deriveBaseAvailableAmount: Derive = (values) => deriveCaseManagementValues(values).baseAvailableAmount;

// Helper functions
const findPackageSchedule = (package_fixed_id: any) => {
  const { clientPackageSchedules } = store.getState().main;
  return clientPackageSchedules.rows.find(({ package: { id } }) => Number(package_fixed_id) === id);
};

const hasSahBudgets = (client_id: number) => {
  const { sahBudgets } = store.getState().supportAtHome;
  return sahBudgets.rows.some(({ client_id: id }) => id === client_id);
};

// Price plan validator helpers
export const isCategoryValid = (row: PricePlanModalRow | Row, serviceTypeAssocs: any[], categories: any[]) => {
  const { service_type_id, category_id } = row ?? {};

  const assoc = serviceTypeAssocs.find((assoc) => assoc.service_type_ids.includes(service_type_id));
  const category = categories.find((cat) => cat.id === category_id);
  const categoryMatches = assoc?.category_id === category_id;

  return categoryMatches && assoc && category;
};

export const validServiceType = (row: PricePlanModalRow | Row, serviceTypes: any[]) => {
  return serviceTypes.find((st) => st.id === row.service_type_id);
};
